import { html, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import type { CarouselItem, CarouselPresentation } from '../../core/protocol/types';
import { isProxyImageUrl } from '../../core/protocol/validators';
import { toolStyles } from '../styles';
import { ToolElement } from './base';
import { leftIcon, rightIcon } from '../icons';
export class OsviCarousel extends ToolElement {
  static styles = toolStyles;
  protected readonly toolType = 'ui_carousel';
  @property() variant: 'slider' | 'list' = 'slider';
  @state() private index = 0;
  @state() private imageFailed = new Set<number>();
  private pointerStart: number | undefined;
  private suppressPointerClick = false;
  protected willUpdate(changed: Map<PropertyKey, unknown>) {
    if (changed.has('interaction')) { this.index = 0; this.imageFailed = new Set(); this.pointerStart = undefined; this.suppressPointerClick = false; }
  }
  render() {
    const i = this.interaction;
    if (!i) return nothing;
    if (!this.valid) return html`<p>${i.content}</p>`;
    const p = i.presentation as CarouselPresentation;
    const chosen = p.items.find(item => item.id === (i.response_payload?.kind === 'selection' ? i.response_payload.value : i.selected_option_id));
    return html`<p class="label">${p.label}</p>${p.description ? html`<p class="description">${p.description}</p>` : nothing}
      ${!this.live && !this.readonly ? chosen ? html`<p class="answer">${chosen.title}</p>` : nothing : this.variant === 'list'
        ? html`<div class="list">${p.items.map((item, n) => this.card(item, n))}</div>`
        : html`<div @pointerdown=${(e: PointerEvent) => { this.pointerStart = e.clientX; this.suppressPointerClick = false; }} @pointerup=${(e: PointerEvent) => {
          if (this.pointerStart !== undefined && Math.abs(e.clientX - this.pointerStart) > 40) {
            this.suppressPointerClick = true;
            this.navigate(e.clientX < this.pointerStart ? 1 : -1, p.items.length);
          }
          this.pointerStart = undefined;
        }} @pointercancel=${() => { this.pointerStart = undefined; }}>${this.card(p.items[this.index]!, this.index)}</div>
        <div class="navigation"><button type="button" class="round" aria-label="Previous card" ?disabled=${this.index === 0} @click=${() => this.navigate(-1, p.items.length)}>${leftIcon}</button>
          <span class="dots" aria-hidden="true">${p.items.map((_, n) => html`<i class=${n === this.index ? 'on' : ''}></i>`)}</span><span class="sr" aria-live="polite">${this.index + 1} of ${p.items.length}</span>
          <button type="button" class="round" aria-label="Next card" ?disabled=${this.index === p.items.length - 1} @click=${() => this.navigate(1, p.items.length)}>${rightIcon}</button></div>`}`;
  }
  private navigate(delta: number, length: number) { this.index = Math.max(0, Math.min(length - 1, this.index + delta)); }
  private select(event: MouseEvent, id: string) {
    // Keyboard activation has detail 0; a swipe's synthesized pointer click must not select.
    if (this.suppressPointerClick && event.detail !== 0) {
      this.suppressPointerClick = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.respond({ kind: 'selection', value: id });
  }
  private card(item: CarouselItem, index: number) {
    const image = item.image;
    return html`<article class="card">${image && isProxyImageUrl(image.url) ? html`<div class="image">${this.imageFailed.has(index)
      ? html`<span role="img" aria-label="Image unavailable">Image unavailable</span>`
      : html`<img src=${this.baseUrl.replace(/\/$/, '') + image.url} alt=${image.alt} referrerpolicy="no-referrer" loading="lazy" @error=${() => { this.imageFailed = new Set([...this.imageFailed, index]); }}>`}</div>` : nothing}
      <div class="body"><h3>${item.title}</h3>${item.description ? html`<p>${item.description}</p>` : nothing}
      <button type="button" class="primary" ?disabled=${!this.live} @click=${(e: MouseEvent) => this.select(e, item.id)}>${item.action_label ?? 'Select'}</button></div></article>`;
  }
}
